import { PureComponent } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";

export interface RadarPoint {
    stat: string;
    A: number;
    B?: number;
    fullMark: number;
    aRaw?: number;
    bRaw?: number;
}

interface ComparisonEngineChartProps {
    data: RadarPoint[];
    playerAName: string;
    playerBName: string;
    maxDomain?: number;
}

interface TooltipEntry {
    dataKey: string;
    value: number;
    payload: RadarPoint;
}

const formatValue = (value: number): string => (value % 1 === 0 ? value.toString() : value.toFixed(1));

function RadarTooltip({
    active,
    payload,
    playerAName,
    playerBName,
}: {
    active?: boolean;
    payload?: TooltipEntry[];
    playerAName: string;
    playerBName: string;
}) {
    if (!active || !payload?.length) return null;

    const stat = payload[0]?.payload?.stat;
    const aEntry = payload.find((p) => p.dataKey === "A");
    const bEntry = payload.find((p) => p.dataKey === "B");

    return (
        <div className="chart-tooltip">
            <div className="font-semibold text-sm text-on-surface mb-2">{stat}</div>
            <div className="flex flex-col gap-1">
                {aEntry && (
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-chart-blue)]" />
                            <span className="text-xs text-on-surface-variant">{playerAName}</span>
                        </div>
                        <span className="text-sm font-bold text-[var(--color-chart-blue)]">
                            {aEntry.payload.aRaw !== undefined ? formatValue(aEntry.payload.aRaw) : `${aEntry.value}%`}
                        </span>
                    </div>
                )}
                {bEntry && (
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-[var(--color-general-orange)]" />
                            <span className="text-xs text-on-surface-variant">{playerBName}</span>
                        </div>
                        <span className="text-sm font-bold text-[var(--color-general-orange)]">
                            {bEntry.payload.bRaw !== undefined ? formatValue(bEntry.payload.bRaw) : `${bEntry.value}%`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default class ComparisonEngineChart extends PureComponent<ComparisonEngineChartProps> {
    static defaultProps = {
        maxDomain: undefined,
    };

    render() {
        const { data, playerAName, playerBName } = this.props;
        const hasPlayerB = data.length > 0 && Object.prototype.hasOwnProperty.call(data[0], "B") && playerBName;

        return (
            <div className="h-full">
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="45%" outerRadius="75%" data={data}>
                        <PolarGrid gridType="polygon" />
                        <PolarAngleAxis
                            dataKey="stat"
                            tick={{ fontSize: 12, fontWeight: 700, fill: "var(--color-gray-600)" }}
                        />
                        <PolarRadiusAxis domain={[0, 100]} tick={false} tickCount={6} axisLine={false} />
                        <Tooltip
                            content={({ active, payload }) => (
                                <RadarTooltip
                                    active={active}
                                    payload={payload as TooltipEntry[]}
                                    playerAName={playerAName}
                                    playerBName={playerBName}
                                />
                            )}
                        />
                        <Radar
                            name={playerAName}
                            dataKey="A"
                            stroke="var(--color-chart-blue)"
                            fill="var(--color-chart-blue)"
                            fillOpacity={0.5}
                            dot={{ r: 4, strokeWidth: 0, fill: "var(--color-chart-blue)" }}
                            activeDot={{ r: 6, strokeWidth: 2, stroke: "var(--color-chart-blue)", fill: "white" }}
                        />
                        {hasPlayerB && (
                            <Radar
                                name={playerBName}
                                dataKey="B"
                                stroke="var(--color-general-orange)"
                                fill="var(--color-general-orange)"
                                fillOpacity={0.5}
                                dot={{ r: 4, strokeWidth: 0, fill: "var(--color-general-orange)" }}
                                activeDot={{
                                    r: 6,
                                    strokeWidth: 2,
                                    stroke: "var(--color-general-orange)",
                                    fill: "white",
                                }}
                            />
                        )}
                    </RadarChart>
                </ResponsiveContainer>
            </div>
        );
    }
}
